/**
 * Image URL utility with Cloudflare Image Transform support.
 *
 * Handles both full URLs (legacy) and bare R2 keys, and appends
 * /cdn-cgi/image/ transform parameters when ENV.IMAGE_TRANSFORMS_ENABLED.
 */

import { ENV } from '@/config/env';

export type ImageSize = 'thumbnail' | 'avatar' | 'medium' | 'full' | 'original';

const TRANSFORM_PRESETS: Record<Exclude<ImageSize, 'original'>, string> = {
  thumbnail: 'w=150,h=150,fit=cover,format=auto,quality=75',
  avatar: 'w=320,h=320,fit=cover,format=auto,quality=80',
  medium: 'w=600,h=600,fit=scale-down,format=auto,quality=80',
  full: 'w=1200,fit=scale-down,format=auto,quality=85',
};

// A path that already carries transform params ("cdn-cgi/image/<params>/<key>").
// Stripped before re-applying a preset so callers can pass an already
// transformed URL without producing a nested, invalid transform path.
const TRANSFORM_SEGMENT = /^cdn-cgi\/image\/[^/]+\//;

/**
 * Build an image URL with optional Cloudflare Image Transforms.
 *
 * @param r2KeyOrUrl - An R2 object key ("profile-photos/abc.webp") or a full URL.
 * @param size       - Preset size. Defaults to 'original' (no transforms).
 * @returns The resolved URL string, or empty string for null/undefined input.
 */
export function imageUrl(
  r2KeyOrUrl: string | null | undefined,
  size: ImageSize = 'original',
): string {
  if (!r2KeyOrUrl) return '';

  // Determine the base origin and path
  let origin: string;
  let path: string;

  if (r2KeyOrUrl.startsWith('http://') || r2KeyOrUrl.startsWith('https://')) {
    // Already a full URL -- extract origin and path
    try {
      const parsed = new URL(r2KeyOrUrl);
      origin = parsed.origin;
      path = parsed.pathname.replace(/^\//, '').replace(TRANSFORM_SEGMENT, '');
    } catch {
      // Malformed URL -- return as-is
      return r2KeyOrUrl;
    }
  } else {
    // Bare R2 key
    if (!ENV.UPLOAD_URL) {
      // Local development -- serve through the gateway at /uploads/{key}
      return `${ENV.API_URL}/uploads/${r2KeyOrUrl}`;
    }
    origin = ENV.UPLOAD_URL;
    path = r2KeyOrUrl;
  }

  // Transforms are only served where the zone has them enabled (see env.ts)
  if (size === 'original' || !ENV.IMAGE_TRANSFORMS_ENABLED) {
    return `${origin}/${path}`;
  }

  const params = TRANSFORM_PRESETS[size];
  return `${origin}/cdn-cgi/image/${params}/${path}`;
}
